import type { EmployeeTimeEntryType } from '../components/employees/employeeTimeEntryConstants';

export type PendingChangeRequestInfo = {
  action: 'upsert' | 'delete';
  entry_type: EmployeeTimeEntryType | null;
  start_time: string | null;
  end_time: string | null;
  break_minutes: number;
};

export type PendingListEntryFields = {
  entry_type: EmployeeTimeEntryType;
  start_time: string | null;
  end_time: string | null;
  break_minutes: number;
};

export function normalizeChangeRequestDate(value: string): string {
  return String(value ?? '').trim().slice(0, 10);
}

function parseEntryType(value: unknown): EmployeeTimeEntryType | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  return raw as EmployeeTimeEntryType;
}

export function pendingInfoFromChangeRequest(changeRequest: {
  entry_date: string;
  action: 'upsert' | 'delete';
  proposed_payload?: Record<string, unknown> | null;
  previous_snapshot?: Record<string, unknown> | null;
}): { date: string; info: PendingChangeRequestInfo } {
  const date = normalizeChangeRequestDate(changeRequest.entry_date);
  if (changeRequest.action === 'delete') {
    const previous = changeRequest.previous_snapshot ?? {};
    return {
      date,
      info: {
        action: 'delete',
        entry_type: parseEntryType(previous.entry_type),
        start_time: previous.start_time != null ? String(previous.start_time) : null,
        end_time: previous.end_time != null ? String(previous.end_time) : null,
        break_minutes: Number.isFinite(Number(previous.break_minutes))
          ? Number(previous.break_minutes)
          : 0,
      },
    };
  }

  const proposed = changeRequest.proposed_payload ?? {};
  return {
    date,
    info: {
      action: 'upsert',
      entry_type: parseEntryType(proposed.entry_type),
      start_time: proposed.start_time != null ? String(proposed.start_time) : null,
      end_time: proposed.end_time != null ? String(proposed.end_time) : null,
      break_minutes: Number.isFinite(Number(proposed.break_minutes))
        ? Number(proposed.break_minutes)
        : 0,
    },
  };
}

export function buildPendingChangeRequestMap(
  items: Array<{
    employee_id: string;
    entry_date: string;
    action: 'upsert' | 'delete';
    proposed_payload?: Record<string, unknown> | null;
  }>,
  employeeId: string,
): Map<string, PendingChangeRequestInfo> {
  const map = new Map<string, PendingChangeRequestInfo>();
  for (const item of items) {
    if (String(item.employee_id) !== String(employeeId)) continue;
    const date = normalizeChangeRequestDate(item.entry_date);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const proposed = item.proposed_payload ?? {};
    map.set(date, {
      action: item.action,
      entry_type: item.action === 'delete' ? null : parseEntryType(proposed.entry_type),
      start_time:
        item.action === 'delete'
          ? null
          : proposed.start_time != null
            ? String(proposed.start_time)
            : null,
      end_time:
        item.action === 'delete' ? null : proposed.end_time != null ? String(proposed.end_time) : null,
      break_minutes:
        item.action === 'delete'
          ? 0
          : Number.isFinite(Number(proposed.break_minutes))
            ? Number(proposed.break_minutes)
            : 0,
    });
  }
  return map;
}

export function hasPendingChangeRequestForDate(
  map: Map<string, PendingChangeRequestInfo>,
  dateKey: string,
): boolean {
  return map.has(normalizeChangeRequestDate(dateKey));
}

export function effectiveEntryTypeForPendingDay(
  dateKey: string,
  currentType: EmployeeTimeEntryType | null | undefined,
  map: Map<string, PendingChangeRequestInfo>,
): EmployeeTimeEntryType | null {
  const pending = map.get(normalizeChangeRequestDate(dateKey));
  if (!pending) return currentType ?? null;
  if (pending.action === 'delete') return currentType ?? null;
  return pending.entry_type ?? currentType ?? null;
}

export function resolvePendingListEntryFields(
  entry: PendingListEntryFields,
  dateKey: string,
  map: Map<string, PendingChangeRequestInfo>,
): PendingListEntryFields {
  const pending = map.get(normalizeChangeRequestDate(dateKey));
  if (!pending || pending.action === 'delete') return entry;
  return {
    entry_type: pending.entry_type ?? entry.entry_type,
    start_time: pending.start_time ?? entry.start_time,
    end_time: pending.end_time ?? entry.end_time,
    break_minutes: pending.break_minutes ?? entry.break_minutes,
  };
}
